/**
 * Boundary transform for a backend grid-spec descriptor stat card.
 *
 * The sibling of normalizeDescriptorColumn for the aggregate cards a grid
 * renders above itself: the single place that reads the raw `/grid-spec/`
 * `stat_cards` shape (snake_case, nullable) and turns it into the stable,
 * defaulted, camelCase card the grid renders. A new backend key flows
 * through here without touching a view, and a card whose `type` this bundle
 * has no renderer for still carries its number — see resolveStatCardRenderer.
 *
 * Contains:
 * - STAT_CARD_TONES — the tone tokens the backend contract emits
 * - normalizeDescriptorStatCard — descriptor card → normalized card
 * - buildStatCardVisibilityModel — descriptor default_hidden → visibility model
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * Semantic tone tokens the backend emits for a stat card. A token outside
 * this set is normalized to `neutral` rather than passed through, so an
 * unrecognized tone renders as an unstyled card instead of a missing colour.
 */
export const STAT_CARD_TONES = Object.freeze(['positive', 'negative', 'attention', 'neutral', 'info']);

const DEFAULT_TONE = 'neutral';

/**
 * The card type a descriptor carrying none is read as. An unrecognized type is
 * passed through rather than coerced here: which types this bundle can draw is
 * the renderer registry's question, and coercing at the boundary would hide a
 * type an injected registry does know how to draw.
 */
const DEFAULT_TYPE = 'count';

/**
 * Humanize a stat-card `key` into a readable label for the case where the
 * descriptor's `label_key` has no i18n entry and no literal `label` was sent.
 * Keys are dotted paths into the stats payload, so the leaf segment is the
 * meaningful part: `status_counts.impacted_digital` → "Impacted Digital".
 *
 * @param {string} key
 * @returns {string}
 */
function humanizeKey(key) {
  if (!key || typeof key !== 'string') return key ?? '';
  const leaf = key.split('.').pop();
  return leaf
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The filter pairs a card stands for, as one map.
 *
 * `filters` is the whole of a card's click — one pair for a card over a single
 * dimension, several for one describing an intersection ("alarming digitals").
 * A card that only reports a number carries none, and reads as an empty map
 * rather than undefined so no consumer has to guard the shape.
 *
 * @param {?Object<string, string>} filters - the descriptor's `filters`
 * @returns {Object<string, string>} Possibly empty, never undefined.
 */
function normalizeStatCardFilters(filters) {
  if (filters && typeof filters === 'object') return Object.fromEntries(Object.entries(filters));
  return {};
}

/**
 * Normalize one descriptor stat card into the renderer's camelCase shape.
 *
 * @param {object} specCard - One entry from the descriptor `stat_cards` array.
 * @returns {{ cardId: string, key: string, type: string,
 *   comparisonKey: (string|undefined), seriesKey: (string|undefined),
 *   headerLabel: string, headerFallback: string,
 *   tooltipLabel: (string|undefined),
 *   tooltipFallback: (string|undefined), tone: string,
 *   filters: Object<string, string>,
 *   defaultHidden: boolean, hideable: boolean }}
 */
export function normalizeDescriptorStatCard(specCard) {
  const {
    id,
    key,
    type,
    comparison_key: comparisonKey,
    series_key: seriesKey,
    label_key: labelKey,
    label,
    tooltip_key: tooltipKey,
    tooltip,
    tone,
    filters,
    default_hidden: defaultHidden,
    hideable,
  } = specCard ?? {};

  // Label source: the descriptor's label_key, falling back to the stats key.
  // Resolution through the translator stays with the renderer (it owns `t`).
  const headerLabel = labelKey ?? key;
  // Label text shown when label_key has no translation: the descriptor's
  // literal `label`, else a humanized key leaf.
  const headerFallback = label ?? humanizeKey(key);

  return {
    // Identity, distinct from the stats path: several cards may read one
    // number under different vocabulary, and each stays independently
    // toggleable. Falls back to the path, which is unique when it backs one card.
    cardId: id ?? key,
    key,
    // Names the renderer that draws this card, and which of the keys below it
    // reads. Which types this bundle can draw: resolveStatCardRenderer.
    type: type ?? DEFAULT_TYPE,
    // The further payload paths a type reads. Each is present only on the type
    // that declares it, so both stay undefined on a plain count card.
    comparisonKey: comparisonKey ?? undefined,
    seriesKey: seriesKey ?? undefined,
    headerLabel,
    headerFallback,
    // The hover explanation of what this number counts. Interpolates the same
    // {{term}} the label does, so a tooltip naming the tenant's own word for a
    // structure stays in step with the label above it.
    tooltipLabel: tooltipKey ?? undefined,
    tooltipFallback: tooltip ?? undefined,
    tone: STAT_CARD_TONES.includes(tone) ? tone : DEFAULT_TONE,
    // Every pair a click applies, and every pair "this card is the applied
    // one" is read from. Always a map, empty for a card that only reports.
    filters: normalizeStatCardFilters(filters),
    defaultHidden: Boolean(defaultHidden),
    // hideable defaults true when the descriptor omits the key; only an
    // explicit `false` locks the card shown.
    hideable: hideable !== false,
  };
}

/**
 * Build the initial stat-card visibility model from the descriptor: every card
 * flagged `default_hidden` starts hidden. Mirrors buildColumnVisibilityModel so
 * cards and columns share one precedence story.
 *
 * @param {object[]} specStatCards - The descriptor's raw `stat_cards` array.
 * @returns {Object<string, boolean>} `{ [cardId]: false }` for default-hidden cards.
 */
export function buildStatCardVisibilityModel(specStatCards) {
  if (!Array.isArray(specStatCards)) return {};
  const model = {};
  for (const card of specStatCards) {
    const { cardId, defaultHidden } = normalizeDescriptorStatCard(card);
    if (defaultHidden) model[cardId] = false;
  }
  return model;
}

/**
 * Resolve a stat card's value out of the `/stats/` payload by its dotted key.
 * A missing path reads 0 rather than undefined: the backend omits a key whose
 * card the tenant may not see, and a card the descriptor did emit but the
 * payload has not yet delivered is loading, not broken.
 *
 * @param {?object} stats - The `/stats/` payload, or null before it lands.
 * @param {string} key - Dotted path (`status_counts.impacted_digital`).
 * @returns {number} The value at `key`, or 0.
 */
export function resolveStatCardValue(stats, key) {
  if (!stats || typeof key !== 'string') return 0;
  let cursor = stats;
  for (const segment of key.split('.')) {
    if (cursor == null || typeof cursor !== 'object') return 0;
    cursor = cursor[segment];
  }
  return typeof cursor === 'number' ? cursor : 0;
}
